// chargePendingPayment — the shared charge primitive for a pending booking_payments row.
//
// 3f 2d-ii Step 1 (2A-i): EXTRACTED verbatim from handleBookConfirmation's two charge branches
// (saved-card off-session + Stripe Checkout redirect) so the activity-join path (Step 4) reuses
// the EXACT booking charge logic instead of duplicating it (Lesson 38). This is a pure code-move:
// the edge-fn invocations + the saved-card paid-update are identical to the inline booking code;
// only their location changed. The caller keeps all entity-specific work (reserve / promote /
// cancel for bookings; leave+decrement for the join in Step 4).
//
//   savedCardId present → invoke charge-saved-card (off-session MIT); on success the pending row
//     is marked paid here (as the booking branch did inline) → { status:'paid' }; decline/network
//     → { status:'failed' } (caller decides the entity reaction).
//   savedCardId absent  → invoke create-checkout-session → { status:'redirect', url } (the webhook
//     promotes the row to paid later); no url/error → { status:'failed' }.
//
// NOTE: the request bodies are byte-identical to the pre-extraction booking calls — charge-saved-card
// gets amount_cents (öre) + currency 'sek' + description + related_entity + payment_method_id;
// create-checkout-session gets booking_id + amount (kr = amountMinor/100, exact for ≤2-decimal kr) +
// payment_id + success/cancel urls. The charge_enabled guard (Step 3) lives in the edge functions,
// so every caller of this helper inherits it automatically.

import { supabase as defaultSb } from "@/integrations/supabase/client";
import type { SupabaseClient } from "@supabase/supabase-js";

export interface ChargePendingPaymentInput {
  /** The paying user. */
  userId: string;
  /** Org that owns the charge (per-org Stripe resolved server-side). */
  orgId: string;
  /** Amount in öre (minor units). */
  amountMinor: number;
  /** The pending booking_payments row to settle on a successful off-session charge. */
  paymentId: string;
  /** A saved MIT card id → off-session charge. Absent/null → Stripe Checkout redirect. */
  savedCardId?: string | null;
  /** Stripe charge description (e.g. `Bokning <courtId>`). */
  description: string;
  /** 'booking' | 'activity' — drives the Step-3 activity-scoped charge_enabled guard. */
  relatedEntityType: string;
  /** The booking/activity id the charge relates to. */
  relatedEntityId: string;
  /** Checkout-path only: the booking id create-checkout-session reserves against. */
  bookingId?: string;
  /** Checkout-path only: Stripe redirect URLs. */
  successUrl?: string;
  cancelUrl?: string;
  /** Injectable Supabase client (tests); defaults to the app client. */
  sb?: SupabaseClient;
}

export type ChargePendingPaymentResult =
  | { status: "paid"; paymentIntentId: string | null }
  | { status: "redirect"; url: string }
  // 2d-ii Step 4: the org's per-org charge_enabled kill-switch (Step 3) is OFF for an ACTIVITY
  // charge → charge-saved-card returns {skipped:"charge_disabled"}. NOT a failure (intentional) —
  // the caller leaves the pending row + the player joined (= AP's safe 2d-i state). Bookings never
  // reach this (they send relatedEntityType='booking'; the Step-3 guard only gates 'activity').
  | { status: "skipped" }
  | { status: "failed"; error: string; network: boolean };

export async function chargePendingPayment(
  input: ChargePendingPaymentInput,
): Promise<ChargePendingPaymentResult> {
  const sb = input.sb ?? defaultSb;

  if (input.savedCardId) {
    // ── Saved card: off-session MIT (verbatim from the saved_card: branch) ──
    try {
      const { data: chargeResult, error: chargeError } = await sb.functions.invoke("charge-saved-card", {
        body: {
          user_id: input.userId,
          organization_id: input.orgId,
          amount_cents: input.amountMinor,
          currency: "sek",
          description: input.description,
          related_entity_type: input.relatedEntityType,
          related_entity_id: input.relatedEntityId,
          payment_method_id: input.savedCardId,
        },
      });
      const cr = chargeResult as { success?: boolean; error?: string; skipped?: string } | null;
      // 2d-ii Step 3 kill-switch: an activity charge against a non-armed org returns
      // {skipped:"charge_disabled"} (no `success`). Surface it distinctly — NOT a decline.
      // (Bookings never hit this branch: they send 'booking' → the guard bypasses them.)
      if (cr?.skipped === "charge_disabled") {
        return { status: "skipped" };
      }
      if (chargeError || !cr?.success) {
        // Decline: carry chargeResult.error verbatim (the booking branch's toast was
        // `chargeResult?.error || "Betalningen misslyckades"` — the caller reproduces that).
        return {
          status: "failed",
          error: cr?.error ?? "",
          network: false,
        };
      }
      const paymentIntentId =
        (chargeResult as { payment_intent_id?: string }).payment_intent_id ?? null;
      await sb
        .from("booking_payments")
        .update({
          status: "paid",
          paid_at: new Date().toISOString(),
          stripe_payment_intent_id: paymentIntentId,
        } as Record<string, unknown>)
        .eq("id", input.paymentId);
      return { status: "paid", paymentIntentId };
    } catch (err) {
      // Network error — caller cancels/rolls back (booking shows its fixed "…Försök igen." copy).
      return { status: "failed", error: (err as Error)?.message ?? "", network: true };
    }
  }

  // ── No saved card: Stripe Checkout redirect (verbatim from the stripe branch) ──
  try {
    const { data: checkoutData, error: checkoutError } = await sb.functions.invoke("create-checkout-session", {
      body: {
        booking_id: input.bookingId,
        amount: input.amountMinor / 100,
        payment_id: input.paymentId,
        success_url: input.successUrl,
        cancel_url: input.cancelUrl,
      },
    });
    if (checkoutError) {
      // 2d-iii B2 (B1 parity): create-checkout-session returns 403 {skipped:"charge_disabled"}
      // for an ACTIVITY charge against an unarmed org — the same intentional skip as the
      // saved-card branch above, NOT a failure (no redirect attempt, no error UX). supabase-js
      // surfaces non-2xx as FunctionsHttpError with the Response on .context. Bookings never
      // reach this (the B1 guard only gates activity payments) — booking path unchanged.
      const ctx = (checkoutError as { context?: Response }).context;
      if (ctx && typeof ctx.json === "function") {
        try {
          const body = (await ctx.json()) as { skipped?: string } | null;
          if (body?.skipped === "charge_disabled") {
            return { status: "skipped" };
          }
        } catch {
          // non-JSON error body — fall through to the failed path below
        }
      }
      throw checkoutError;
    }
    if ((checkoutData as { url?: string } | null)?.url) {
      return { status: "redirect", url: (checkoutData as { url: string }).url };
    }
    throw new Error("Stripe Checkout: ingen redirect-URL returnerad");
  } catch (err) {
    // Checkout-start failure — booking shows its fixed "Kunde inte starta kortbetalning…" copy.
    return { status: "failed", error: (err as Error)?.message ?? "", network: true };
  }
}
